import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
    setCameraOptions,
    setMapLoaded,
    setMapPosition,
    setFitBounds,
    setMapProperties
} from '../../redux/map/Actions';

import { compose } from 'redux';
import { SimplifyFilters } from '../../redux/layer_settings/Actions';

// A HOC to automatically manage a bunch of standard redux state for the map
function withMapInteraction(WrappedComponent) {
    class WithMapInteraction extends React.Component {
        static propTypes = {
            id: PropTypes.string.isRequired,
            baseLayer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            onMapLoaded: PropTypes.func,
            events: PropTypes.object,
            // Redux state
            cameraOptions: PropTypes.object,
            activeLayers: PropTypes.array.isRequired,
            // layers: PropTypes.array.isRequired,
            mapProperties: PropTypes.object,
            // highlights: PropTypes.array.isRequired,
            // Redux dispatch
            setMapPosition: PropTypes.func.isRequired,
            dispatchSetMapLoaded: PropTypes.func.isRequired,
            dispatchSetFitBounds: PropTypes.func.isRequired,
            dispatchSetMapProperties: PropTypes.func.isRequired,
            dispatchSetCameraOptions: PropTypes.func.isRequired
        };

        static defaultProps = {
            id: 'map'
        };

        constructor(props) {
            super(props);

            this.handleMapLoaded = this.handleMapLoaded.bind(this);
            this.handleMapTouchStart = this.handleMapTouchStart.bind(this);
            this.handleMapTouchMove = this.handleMapTouchMove.bind(this);
            this.handleMapTouchEnd = this.handleMapTouchEnd.bind(this);
            this.handleMapTouchCancel = this.handleMapTouchCancel.bind(this);
            this.handleMapPositionChanged = this.handleMapPositionChanged.bind(this);

            this.events = {
                onTouchStart: this.handleMapTouchStart,
                onTouchEnd: this.handleMapTouchEnd,
                onTouchMove: this.handleMapTouchMove,
                onTouchCancel: this.handleMapTouchCancel,

                onResize: this.handleMapPositionChanged,
                onMoveEnd: this.handleMapPositionChanged,
                onZoomEnd: this.handleMapPositionChanged,
                onPitchEnd: this.handleMapPositionChanged,
                onBoxZoomEnd: this.handleMapPositionChanged,
                onRotateEnd: this.handleMapPositionChanged,
                // Assume events cannot change at the moment, otherwise needs do be done on update or render
                ...props.events
            };
        }

        componentDidMount() {
            const { mapProperties, fitBounds, dispatchSetMapProperties } =
                this.props;
            if (mapProperties || fitBounds) {
                // Map properties already set from something, probably a bookmark
            } else {
                const properties = {
                    center: [-1.898575, 52.489471], // Birmingham
                    zoom: [10],
                    pitch: [0],
                    bearing: [0]
                };
                dispatchSetMapProperties(properties);
            }
        }

        componentWillUnmount() {
            const {
                cameraOptions,
                dispatchSetFitBounds,
                dispatchSetMapProperties,
                dispatchSetMapLoaded
            } = this.props;
            // If the component unmounts, cache the last position. This seems like the most correct way of doing this
            // Not sure if there are any rules or side effects for running redux in will unmount
            // May need to wrap into a single function to prevent race conditions or prevent the value been set while loaded is false
            dispatchSetMapLoaded(false);
            // If the map has loaded and has a new position then cache that position
            if (cameraOptions) {
                dispatchSetMapProperties({
                    center: [cameraOptions.lng, cameraOptions.lat],
                    zoom: [cameraOptions.zoom],
                    pitch: [cameraOptions.pitch],
                    bearing: [cameraOptions.bearing]
                });
                dispatchSetFitBounds(undefined, undefined);
            }
        }

        handleMapLoaded() {
            const { onMapLoaded, dispatchSetMapLoaded } = this.props;
            dispatchSetMapLoaded(true);
            onMapLoaded && onMapLoaded();
        }

        handleMapTouchStart(map, evt) {
            if (evt.points.length === 2) {
                const diff = Math.abs(evt.points[0].y - evt.points[1].y);
                if (diff <= 50) {
                    evt.originalEvent.preventDefault(); // prevent browser refresh on pull down
                    map.touchZoomRotate.disable(); // disable native touch controls
                    map.dragPan.disable();
                    this.dpPoint = evt.point;
                    this.dpPitch = map.getPitch();
                }
            }
            this.dpMoved = false;
        }

        handleMapTouchMove(map, evt) {
            if (this.dpPoint) {
                evt.preventDefault();
                evt.originalEvent.preventDefault();
                const diff = (this.dpPoint.y - evt.point.y) * 0.5;
                map.setPitch(this.dpPitch + diff);
            }
            this.dpMoved = true;
        }

        handleMapTouchEnd(map, evt) {
            if (this.dpPoint) {
                map.touchZoomRotate.enable();
                map.dragPan.enable();
                this.dpPoint = null;
            } else {
                // if (!this.dpMoved)
                //     this.handleMapClick(map, evt);
            }
        }

        handleMapTouchCancel(map, evt) {
            if (this.dpPoint) {
                map.touchZoomRotate.enable();
                map.dragPan.enable();
                this.dpPoint = null;
            }
            this.dpMoved = false;
        }

        handleMapPositionChanged(map, evt) {
            const { setMapPosition } = this.props;

            if (map) {
                const mapWidth = map._container.clientWidth;
                const mapHeight = map._container.clientHeight;
                const tl = map.unproject([0, 0]);
                const tr = map.unproject([mapWidth, 0]);
                const br = map.unproject([mapWidth, mapHeight]);
                const bl = map.unproject([0, mapHeight]);
                // if ((mapPosition && mapProperties.fitBounds) && _.isEqual(mapProperties.fitBounds, [
                //     [...mapPosition[1]],
                //     [...mapPosition[3]],
                // ])) {
                //     console.log('woooop')
                // }
                setMapPosition([
                    [tl.lng, tl.lat],
                    [tr.lng, tr.lat],
                    [br.lng, br.lat],
                    [bl.lng, bl.lat],
                    [tl.lng, tl.lat] // useful for converting these points to a polygon
                ]);

                this.props.dispatchSetCameraOptions({
                    lng: map.getCenter().lng,
                    lat: map.getCenter().lat,
                    zoom: map.getZoom(),
                    pitch: map.getPitch(),
                    bearing: map.getBearing()
                });
            }
        }

        render() {
            const { setMapPosition, onMapLoaded, events, ...props } = this.props;
            return (
                <WrappedComponent
                    {...props}
                    onMapLoaded={this.handleMapLoaded}
                    factoryParameters={{
                        interactive: true,
                        touchZoomRotate: true
                    }}
                    events={this.events}
                />
            );
        }
    }

    const wrappedComponentName =
        WrappedComponent.displayName || WrappedComponent.name || 'Component';

    WithMapInteraction.displayName = `withMapInteraction(${wrappedComponentName})`;
    return WithMapInteraction;
}

const mapStateToProps = (state, ownProps) => {
    const mapReducer = state[ownProps.id];
    // ToDo This should be moved out of this HOC as its functionality doesn't match the expected behaviour
    // This is responsible for combining an active layer with styling and filters and highlights for standalone map
    let activeLayers = state.activeLayers;

    activeLayers = activeLayers.filter(layer => !layer.ignoreAttribution);

    activeLayers = activeLayers.map(el => {
        const layer = state.layers.layers.find(e => e.system_name === el.systemName);
        if (!layer) {
            console.error(`${el.systemName} does not exist for some reason`);
            return;
        }

        const settings = state.layerSettingsReducer.find(
            setting => setting.uid === layer.system_name
        );
        const style = layer.styles[(settings && settings.currentStyleIdx) || 0];
        return {
            ...el,
            layerData: {
                ...layer.layerData,
                filters:
                    layer.filterConfig && settings && settings.filters
                        ? SimplifyFilters(
                              layer.filterConfig.fields,
                              settings.filters.children
                          )
                        : null
            },
            styleIdx: style.id,
            styling: style.mapboxStyleSpecification,
            primaryKey: layer.properties.id,
            highlights: state.highlights.highlightedFeatures.filter(
                e => e.layerSystemName === layer.system_name
            )
        };
    });

    return {
        cameraOptions: mapReducer.cameraOptions,
        activeLayers: activeLayers,
        mapProperties: mapReducer.mapProperties,
        mapPosition: mapReducer.mapPosition,
        fitBounds: mapReducer.fitBounds,
        fitBoundsOptions: mapReducer.fitBoundsOptions
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    dispatchSetMapLoaded: isLoaded => dispatch(setMapLoaded(isLoaded, ownProps.id)),
    dispatchSetFitBounds: (bounds, options = {}) =>
        dispatch(setFitBounds(bounds, options, ownProps.id)),
    dispatchSetMapProperties: properties =>
        dispatch(setMapProperties(properties, ownProps.id)),
    dispatchSetCameraOptions: options =>
        dispatch(setCameraOptions(options, ownProps.id)),
    setMapPosition: mapPosition => dispatch(setMapPosition(mapPosition, ownProps.id))
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withMapInteraction
);
